import { Box, Paper, Typography, Switch, FormControlLabel, Divider, Alert } from "@mui/material";
import { UserTitle } from "../components/UserTitle";
import { UserProfileUpdate } from "../components/UserProfileUpdate";
import { useUserContext } from "../context";
import { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ClientProvider from "../../../modules/ClientProvider";
import { DisplayBoxBase } from "../../../components/shared/StyledComponents";

export const SettingsPreferences = () => {
    const { userContext, loadUserContext } = useUserContext();
    const { getAccessTokenSilently } = useAuth0();
    const userHttpClient = ClientProvider.provideUserClient();
    
    const [openToAdvising, setOpenToAdvising] = useState(userContext.dbUser?.open_to_advising ?? false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [updateSuccess, setUpdateSuccess] = useState(false);

    // Sync local state with user context updates
    useEffect(() => {
        setOpenToAdvising(userContext.dbUser?.open_to_advising ?? false);
    }, [userContext.dbUser?.open_to_advising]);

    const handleOpenToAdvisingChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;
        setIsUpdating(true);
        setUpdateError(null);
        setUpdateSuccess(false);
        
        // Optimistically update the UI
        setOpenToAdvising(newValue);
        
        try {
            const accessToken = await getAccessTokenSilently();
            const { data, error } = await userHttpClient.updateSelf({ open_to_advising: newValue }, accessToken);
            
            if (error) {
                console.error('Error updating open_to_advising:', error);
                setUpdateError('Failed to update setting. Please try again.');
                // Revert to previous state on error
                setOpenToAdvising(!newValue);
            } else {
                setUpdateSuccess(true);
                // Refresh user context to ensure consistency
                await loadUserContext();
                // Clear success message after 3 seconds
                setTimeout(() => setUpdateSuccess(false), 3000);
            }
        } catch (error) {
            console.error('Error updating open_to_advising:', error);
            setUpdateError('Failed to update setting. Please try again.');
            // Revert to previous state on error
            setOpenToAdvising(!newValue);
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <Box>
            <UserTitle section="Preferences" />
            
            {updateSuccess && (
                <Alert severity="success" sx={{ marginBottom: '20px' }}>
                    Settings updated successfully!
                </Alert>
            )}
            
            {updateError && (
                <Alert severity="error" sx={{ marginBottom: '20px' }} onClose={() => setUpdateError(null)}>
                    {updateError}
                </Alert>
            )}

            <DisplayBoxBase className="column white left">
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Advisory Preferences
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '20px' }}>
                    Manage your availability for new advisory opportunities.
                </Typography>
                
                <FormControlLabel
                    control={
                        <Switch
                            checked={openToAdvising}
                            onChange={handleOpenToAdvisingChange}
                            disabled={isUpdating}
                            color="primary"
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                Open to new advisory opportunities
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                When enabled, you may receive invitations to provide advisory services for new companies.
                            </Typography>
                        </Box>
                    }
                    sx={{ alignItems: 'flex-start', margin: 0 }}
                />
            </DisplayBoxBase>

        </Box>
    );
}